import { apiClient } from "./client"
import type { GetSignedURLsResponse } from "@/shared/types/api"

// Types for image upload
interface PrepareUploadRequest {
  fileName: string
  fileSize: number
  mimeType: string
  fileHash?: string
  clientId?: string
}

interface PrepareUploadResponse {
  uploadUrl: string
  mimeType: string
  publicUrl: string
  confirmToken: string
  expiresAt: string
}

interface ConfirmUploadRequest {
  confirmToken: string
}

interface ImageUploadResult {
  id: string
  publicUrl: string
  status: string
}

export const imageApi = {
  /**
   * Get signed URLs for viewing/downloading multiple images
   * @param imageIds - Array of image IDs to fetch
   * @returns Promise with signed URLs, notFound, and unauthorized arrays
   */
  getSignedUrls: async (imageIds: string[]): Promise<GetSignedURLsResponse> => {
    const response = await apiClient.get<GetSignedURLsResponse>('/images/signed-urls', {
      params: {
        image_ids: imageIds.join(','),
      },
    })
    return response.data
  },

  /**
   * Prepare an image upload - gets presigned URL from R2
   * @param request - Upload preparation details
   * @returns Presigned URL and confirmation token
   */
  prepareUpload: async (request: PrepareUploadRequest): Promise<PrepareUploadResponse> => {
    const response = await apiClient.post<{ data: PrepareUploadResponse }>('/images/prepare-upload', request)
    return response.data.data
  },

  /**
   * Confirm an image upload after uploading to R2
   * @param imageId - The image ID from prepare-upload
   * @param confirmToken - The confirmation token
   * @returns The confirmed image record
   */
  confirmUpload: async (imageId: string, confirmToken: string): Promise<ImageUploadResult> => {
    const request: ConfirmUploadRequest = { confirmToken }
    const response = await apiClient.post<ImageUploadResult>(`/images/${imageId}/confirm`, request)
    return response.data
  },

  /**
   * Upload a file to R2 storage
   * This is a complete flow: prepare -> upload to R2 -> confirm
   * @param file - The file to upload
   * @returns The image ID that can be used in other API calls
   */
  uploadFile: async (file: File): Promise<string> => {
    // 1. Prepare the upload
    const prepareResponse = await imageApi.prepareUpload({
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type || 'image/jpeg',
    })

    // 2. Upload directly to R2 using presigned URL
    const uploadResponse = await fetch(prepareResponse.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': prepareResponse.mimeType,
      },
    })

    if (!uploadResponse.ok) {
      throw new Error(`Failed to upload to R2: ${uploadResponse.statusText}`)
    }

    // 3. Extract image ID from the public URL
    // URL format: uploads/{user_id}/{year}/{month}/{image_id}.{ext}
    const urlParts = prepareResponse.publicUrl.split('/')
    const filename = urlParts[urlParts.length - 1]
    const imageId = filename.split('.')[0]

    // 4. Confirm the upload
    await imageApi.confirmUpload(imageId, prepareResponse.confirmToken)

    return imageId
  },

  /**
   * Upload multiple files to R2 storage
   * @param files - Array of files to upload
   * @returns Array of image IDs
   */
  uploadFiles: async (files: File[]): Promise<string[]> => {
    const imageIds: string[] = []

    for (const file of files) {
      const imageId = await imageApi.uploadFile(file)
      imageIds.push(imageId)
    }

    return imageIds
  },
}
